/**
 * XHR-based upload helper that exposes per-request upload progress events.
 *
 * `fetch` does not expose `upload.onprogress`, so anywhere we want to show a
 * percentage while a large multipart body is going out (photo posts, video
 * uploads, etc.) we route through this helper instead of `api.post`. The
 * response is parsed using the same Yaarikut envelope contract as the typed
 * `api` client in `@/lib/api/client.ts` — `success: true` unwraps `data`,
 * anything else throws an `ApiError`.
 */

import { ApiError, buildUrl, getAuthToken, notifyUnauthorized } from '@/lib/api/client';
import type { ApiResponse } from '@/types/api';

const API_KEY = import.meta.env.VITE_API_KEY;

export interface UploadProgress {
  /** Bytes uploaded so far. */
  loaded: number;
  /** Total bytes (0 when not computable). */
  total: number;
  /** 0–100 integer percentage; `null` when the server didn't report length. */
  percent: number | null;
}

export interface UploadWithProgressOptions {
  /** Relative API path (e.g. `timeline/post/photo`) — same convention as `api.post`. */
  url: string;
  /** Multipart body to upload. */
  body: FormData;
  /** Optional extra request headers. */
  headers?: Record<string, string>;
  /** HTTP method (default `POST`). */
  method?: 'POST' | 'PUT';
  /** Called repeatedly while the upload streams. */
  onProgress?: (progress: UploadProgress) => void;
  /** Abort signal to cancel the in-flight request. */
  signal?: AbortSignal;
}

/**
 * Upload a multipart body via XHR, emitting progress to `onProgress`. Resolves
 * with the unwrapped envelope `data` (matching `api.post`); rejects with an
 * `ApiError` on network failure, non-OK status, or envelope `success: false`.
 */
export function uploadWithProgress<T>({
  url,
  body,
  headers = {},
  method = 'POST',
  onProgress,
  signal,
}: UploadWithProgressOptions): Promise<T> {
  return new Promise<T>((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    xhr.open(method, buildUrl(url), true);

    xhr.setRequestHeader('Accept', 'application/json');
    if (API_KEY) xhr.setRequestHeader('api-key', API_KEY);
    const token = getAuthToken();
    if (token) xhr.setRequestHeader('Authorization', `Bearer ${token}`);
    for (const [key, value] of Object.entries(headers)) {
      xhr.setRequestHeader(key, value);
    }
    // NOTE: do NOT set Content-Type — the browser sets the multipart boundary.

    // Track the most recent real totals so the final 100% tick below can
    // report the actual byte size rather than a synthetic 1/1 placeholder.
    let lastTotal = 0;
    let lastLoaded = 0;
    if (onProgress) {
      xhr.upload.onprogress = (event) => {
        const total = event.lengthComputable ? event.total : 0;
        const percent =
          event.lengthComputable && total > 0
            ? Math.min(100, Math.round((event.loaded / total) * 100))
            : null;
        if (event.lengthComputable) {
          lastTotal = total;
          lastLoaded = event.loaded;
        }
        onProgress({ loaded: event.loaded, total, percent });
      };
    }

    xhr.onload = () => {
      // Parse the envelope; tolerate empty / non-JSON bodies.
      let envelope: Partial<ApiResponse<T>> | null = null;
      const text = xhr.responseText;
      if (text) {
        try {
          envelope = JSON.parse(text) as ApiResponse<T>;
        } catch {
          envelope = null;
        }
      }

      const code = envelope?.code ?? xhr.status;
      const ok = xhr.status >= 200 && xhr.status < 300;
      const success = envelope?.success ?? ok;

      if (code === 401 || code === 403) {
        notifyUnauthorized();
      }

      if (!success || envelope === null) {
        const message =
          envelope?.message ??
          (ok ? 'Unable to process request' : xhr.statusText) ??
          'Request failed';
        reject(new ApiError(message, code, envelope?.data ?? null));
        return;
      }

      // Final 100% tick — XHR doesn't always fire a progress event with
      // loaded === total when the request finishes very quickly. Use the
      // most recently observed real totals so subscribers see accurate
      // byte counts (falls back to 1/1 only when the server never sent a
      // computable Content-Length).
      if (onProgress) {
        const total = lastTotal > 0 ? lastTotal : 1;
        const loaded = lastTotal > 0 ? lastTotal : Math.max(lastLoaded, 1);
        onProgress({ loaded, total, percent: 100 });
      }
      resolve(envelope.data as T);
    };

    xhr.onerror = () => {
      reject(new ApiError('Network request failed', 0, null));
    };

    xhr.onabort = () => {
      reject(new ApiError('Upload aborted', 0, null));
    };

    if (signal) {
      if (signal.aborted) {
        xhr.abort();
        return;
      }
      signal.addEventListener('abort', () => xhr.abort(), { once: true });
    }

    xhr.send(body);
  });
}
